'use client';

import { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, ArrowUpRight, BarChart3, Sparkles, TrendingUp } from 'lucide-react';
import { ChartPrompt } from '@/components/marketing/ChartPrompt';

const examples = [
  {
    id: 'visualizations',
    label: 'Beautiful charts',
    icon: BarChart3,
    src: '/charts/visualizations.html',
    description: 'A clear comparison, not another spreadsheet.',
  },
  {
    id: 'insights',
    label: 'Clear takeaways',
    icon: Sparkles,
    src: '/charts/insights.html',
    description: 'Find the story behind your numbers.',
  },
  {
    id: 'analysis',
    label: 'Deeper analysis',
    icon: TrendingUp,
    src: '/charts/analysis.html',
    description: 'Explore the questions that come next.',
  },
];

function ChartMotifs() {
  return (
    <div className="marketing-motifs" aria-hidden="true">
      <svg className="marketing-motif-bars" viewBox="0 0 140 140" fill="none">
        <rect x="16" y="66" width="25" height="58" rx="5" fill="#bfd1ff" />
        <rect x="55" y="24" width="25" height="100" rx="5" fill="#648df5" />
        <rect x="94" y="46" width="25" height="78" rx="5" fill="#8caefa" />
      </svg>
      <svg className="marketing-motif-line" viewBox="0 0 180 100" fill="none">
        <path
          d="M10 84L57 54L96 65L162 16"
          stroke="#8aabf8"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="162" cy="16" r="9" fill="#426feb" />
      </svg>
      <div className="marketing-motif-donut" />
    </div>
  );
}

export function ChatTeaser() {
  const [active, setActive] = useState(examples[0].id);
  const selected = examples.find(example => example.id === active) || examples[0];
  return (
    <>
      <section className="marketing-hero">
        <ChartMotifs />
        <div className="marketing-hero-copy">
          <p className="marketing-hero-tag">
            <Sparkles size={14} aria-hidden="true" /> YOUR AI CHART MAKER
          </p>
          <h1>
            Your data,
            <br />
            <span>beautifully presented.</span>
          </h1>
          <p className="marketing-hero-description">
            Turn a spreadsheet into a clear, presentation-ready chart.
            <br className="marketing-desktop-break" /> Less formatting. More getting your point across.
          </p>
          <div className="marketing-hero-actions">
            <Link href="/chat" className="marketing-button">
              Start for free <ArrowUpRight size={18} />
            </Link>
            <a href="#chart-preview" className="marketing-button marketing-button-white">
              See it in action <ArrowRight size={17} />
            </a>
          </div>
          <p className="marketing-fine-print">CSV, Excel or pasted data. No credit card needed.</p>
        </div>
        <div className="marketing-format-strip" aria-label="Made for your next deliverable">
          <span>For the story you need to tell.</span>
          <strong>Presentations</strong>
          <strong>Reports</strong>
          <strong>Class projects</strong>
          <strong>Research</strong>
        </div>
      </section>
      <section id="chart-preview" className="marketing-demo marketing-container" aria-labelledby="demo-heading">
        <div className="marketing-section-heading">
          <p className="marketing-eyebrow">LET THE DATA DO THE TALKING</p>
          <h2 id="demo-heading">
            From rows and columns
            <br />
            to a point worth making.
          </h2>
        </div>
        <div className="marketing-demo-tabs" role="tablist" aria-label="Example outputs">
          {examples.map(example => (
            <button
              key={example.id}
              type="button"
              role="tab"
              id={`demo-tab-${example.id}`}
              aria-selected={active === example.id}
              aria-controls={`demo-panel-${example.id}`}
              tabIndex={active === example.id ? 0 : -1}
              onClick={() => setActive(example.id)}
              onKeyDown={event => {
                const index = examples.findIndex(item => item.id === active);
                const next =
                  event.key === 'ArrowRight'
                    ? (index + 1) % examples.length
                    : event.key === 'ArrowLeft'
                      ? (index + examples.length - 1) % examples.length
                      : event.key === 'Home'
                        ? 0
                        : event.key === 'End'
                          ? examples.length - 1
                          : -1;
                if (next < 0) return;
                event.preventDefault();
                setActive(examples[next].id);
                document.getElementById(`demo-tab-${examples[next].id}`)?.focus();
              }}>
              <example.icon size={17} aria-hidden="true" />
              {example.label}
            </button>
          ))}
        </div>
        <div
          className="marketing-demo-frame"
          role="tabpanel"
          id={`demo-panel-${selected.id}`}
          aria-labelledby={`demo-tab-${selected.id}`}>
          <div className="marketing-demo-caption">
            <span>
              <i /> EXAMPLE OUTPUT
            </span>
            <p>{selected.description}</p>
          </div>
          <iframe src={selected.src} title={`${selected.label} example`} loading="lazy" />
        </div>
        <ChartPrompt id="homepage-chart-prompt" />
      </section>
    </>
  );
}
